/* Gebruikersbeheer — dedicated page voor RBAC-users (sectie 6).
 * Toont alle gebruikers met filters + acties: deactivate/reactivate/soft-delete/end-sessions.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserX, UserCheck, LogOut, ShieldOff, Search, Trash2, RotateCcw, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const ROLE_LABELS = {
  superadmin: "Platform-superadmin",
  platform_admin: "Platform-beheerder",
  directeur: "Directeur",
  beheerder: "Organisatiebeheerder",
  locatiemanager: "Locatiemanager",
  pedagogisch: "Pedagogisch medewerker",
  financieel: "Financieel / administratie",
  administratie: "Administratie",
  planner: "Planner",
  ouder: "Ouder",
  lezer: "Alleen-lezen",
};

const ROLE_TONE = {
  superadmin: "bg-purple-100 text-purple-800",
  platform_admin: "bg-purple-50 text-purple-700",
  directeur: "bg-sky-100 text-sky-800",
  beheerder: "bg-sky-50 text-sky-700",
  locatiemanager: "bg-amber-100 text-amber-800",
  pedagogisch: "bg-emerald-100 text-emerald-800",
  financieel: "bg-blue-100 text-blue-800",
  administratie: "bg-blue-50 text-blue-700",
  planner: "bg-indigo-100 text-indigo-800",
  ouder: "bg-rose-100 text-rose-800",
};

export default function GebruikersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("actief");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "pedagogisch" });
  const [inviting, setInviting] = useState(false);

  const submitInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) { toast.error("E-mail en naam verplicht"); return; }
    setInviting(true);
    try {
      await api.post("/users", {
        ...inviteForm,
        email: inviteForm.email.toLowerCase().trim(),
        location_ids: [], group_ids: [], password: "",
      });
      toast.success(`Uitnodiging verstuurd naar ${inviteForm.email}`);
      setInviteOpen(false);
      setInviteForm({ email: "", name: "", role: "pedagogisch" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setInviting(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      const list = Array.isArray(r.data) ? r.data : r.data?.items || [];
      setUsers(list);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter(u => {
      const isDeleted = !!u.deleted_at;
      const isActief = u.active !== false && !isDeleted;
      if (statusFilter === "actief" && !isActief) return false;
      if (statusFilter === "inactief" && (u.active !== false || isDeleted)) return false;
      if (statusFilter === "verwijderd" && !isDeleted) return false;
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (needle && !`${u.name || ""} ${u.email || ""}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [users, q, roleFilter, statusFilter]);

  const doAction = async (uid, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const endpoint = {
        soft_delete: { method: "delete", path: `/users/${uid}` },
        reactivate: { method: "post", path: `/users/${uid}/reactivate` },
        deactivate: { method: "post", path: `/users/${uid}/deactivate` },
        end_sessions: { method: "post", path: `/users/${uid}/end-sessions` },
      }[action];
      await api[endpoint.method](endpoint.path);
      toast.success("Gelukt");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const counts = useMemo(() => {
    const total = users.length;
    const actief = users.filter(u => u.active !== false && !u.deleted_at).length;
    const inactief = users.filter(u => u.active === false && !u.deleted_at).length;
    const verwijderd = users.filter(u => !!u.deleted_at).length;
    return { total, actief, inactief, verwijderd };
  }, [users]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <PageHeader
        title="Gebruikersbeheer"
        subtitle="Beheer gebruikers, rollen, locatie-scoping en actieve sessies."
        actions={
          <Button onClick={() => setInviteOpen(true)}
                  data-testid="invite-user-btn"
                  style={{ background: "var(--sage)" }} className="text-white">
            <UserPlus className="h-4 w-4 mr-1" /> Gebruiker uitnodigen
          </Button>
        }
      />

      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Totaal" value={counts.total} />
        <StatCard label="Actief" value={counts.actief} tone="emerald" testid="stat-actief" />
        <StatCard label="Inactief" value={counts.inactief} tone="amber" testid="stat-inactief" />
        <StatCard label="Verwijderd" value={counts.verwijderd} tone="red" testid="stat-verwijderd" />
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-neutral-600">Zoeken</label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-neutral-400" />
              <Input value={q} onChange={e => setQ(e.target.value)}
                     data-testid="users-search"
                     placeholder="Naam of e-mail…" className="pl-8" />
            </div>
          </div>
          <div>
            <label className="text-xs text-neutral-600">Rol</label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger data-testid="users-role-filter" className="min-w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Alle rollen</SelectItem>
                {Object.entries(ROLE_LABELS).map(([k, v]) =>
                  <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-neutral-600">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="users-status-filter" className="min-w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="actief">Actief</SelectItem>
                <SelectItem value="inactief">Inactief</SelectItem>
                <SelectItem value="verwijderd">Verwijderd (soft-delete)</SelectItem>
                <SelectItem value="all">Alle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-neutral-50 text-xs uppercase text-neutral-600">
              <tr>
                <th className="text-left px-4 py-3">Gebruiker</th>
                <th className="text-left px-4 py-3">Rol</th>
                <th className="text-left px-4 py-3">Locaties</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Acties</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="text-center py-8 text-neutral-500">Laden…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-neutral-500">Geen gebruikers gevonden</td></tr>
              )}
              {filtered.map(u => {
                const deleted = !!u.deleted_at;
                const inactive = u.active === false && !deleted;
                return (
                  <tr key={u.id} className={`border-t border-neutral-100 hover:bg-neutral-50 ${deleted ? 'opacity-60' : ''}`}
                      data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.name || "—"}</div>
                      <div className="text-xs text-neutral-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${ROLE_TONE[u.role] || "bg-neutral-100"} border-none`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {u.location_ids?.length > 0
                        ? <span>{u.location_ids.length} locatie(s)</span>
                        : u.role === "superadmin" || u.role === "beheerder"
                          ? <span className="text-neutral-500 italic">alle</span>
                          : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {deleted ? <Badge className="bg-red-100 text-red-700 border-none">Verwijderd</Badge>
                        : inactive ? <Badge className="bg-amber-100 text-amber-800 border-none">Inactief</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-800 border-none">Actief</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {!deleted && !inactive && (
                          <>
                            <Button size="sm" variant="ghost" title="Beëindig sessies"
                                    onClick={() => doAction(u.id, "end_sessions",
                                      `Actieve sessies van ${u.email} beëindigen?`)}
                                    data-testid={`end-sessions-${u.id}`}>
                              <LogOut className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" title="Deactiveren"
                                    onClick={() => doAction(u.id, "deactivate",
                                      `${u.email} deactiveren?`)}
                                    data-testid={`deactivate-${u.id}`}>
                              <ShieldOff className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {inactive && (
                          <Button size="sm" variant="ghost" title="Reactiveren"
                                  className="text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => doAction(u.id, "reactivate",
                                    `${u.email} weer activeren?`)}
                                  data-testid={`reactivate-${u.id}`}>
                            <UserCheck className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {deleted ? (
                          <Button size="sm" variant="ghost" title="Herstellen"
                                  className="text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => doAction(u.id, "reactivate",
                                    `${u.email} herstellen?`)}
                                  data-testid={`restore-${u.id}`}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" title="Verwijderen (soft-delete)"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => doAction(u.id, "soft_delete",
                                    `${u.email} verwijderen? Audit-trail blijft intact.`)}
                                  data-testid={`soft-delete-${u.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-white" data-testid="invite-user-dialog">
          <DialogHeader>
            <DialogTitle>Nieuwe gebruiker uitnodigen</DialogTitle>
            <DialogDescription>
              De gebruiker ontvangt een 72-uurs activatielink om zelf een wachtwoord in te stellen.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Naam *</Label>
              <Input value={inviteForm.name}
                     onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })}
                     data-testid="invite-name" placeholder="Anna de Vries" />
            </div>
            <div className="col-span-2">
              <Label>E-mailadres *</Label>
              <Input type="email" value={inviteForm.email}
                     onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                     data-testid="invite-email" placeholder="anna@kinderopvang.nl" />
            </div>
            <div className="col-span-2">
              <Label>Rol *</Label>
              <Select value={inviteForm.role}
                      onValueChange={v => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(ROLE_LABELS)
                    // 'superadmin' + 'platform_admin' zijn platform-only rollen — NIET
                    // aanmaakbaar vanuit een KDV. 'ouder' wordt via het aanmeldingsproces
                    // aangemaakt, niet handmatig.
                    .filter(([k]) => !["superadmin", "platform_admin", "ouder"].includes(k))
                    .map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">
                Locaties/groepen kunt u toewijzen na activatie.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>Annuleren</Button>
            <Button onClick={submitInvite} disabled={inviting || !inviteForm.email || !inviteForm.name}
                    data-testid="invite-submit"
                    style={{ background: "var(--sage)" }} className="text-white">
              <UserPlus className="h-4 w-4 mr-1" />
              {inviting ? "Verzenden…" : "Verstuur uitnodiging"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function StatCard({ label, value, tone, testid }) {
  const bg = { emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
                amber: "bg-amber-50 border-amber-200 text-amber-800",
                red: "bg-red-50 border-red-200 text-red-800" }[tone]
              || "bg-neutral-50 border-neutral-200 text-neutral-800";
  return (
    <div className={`rounded-lg border p-3 ${bg}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
